/* analytics.js
   ----------------------------------------------------------------------
   One thin door onto the analytics provider the site already has
   (Firebase Analytics / GA4, initialised in src/firebase.js). Nothing
   else in the codebase should import firebase/analytics directly.

     import { track } from "../lib/analytics";
     track("header_register_interest_click", { locale: "en" });

   Every event carries `page_path` (added here), so reports can be cut
   by page without each caller passing it.

   Events sent site-wide (installed once from App.js, 2026-10-10):
     page_view        { page_path, page_title }   every route change
                      (the landing page is counted by gtag itself)
     whatsapp_click   { text, link }               any wa.me link
     phone_click      { text, link }               any tel: link
     email_click      { text, link }               any mailto: link
     outbound_click   { text, link }               any link to another site
     cta_click        { text, link }               any button, .btn link, or
                      element with data-track="<event name>" (the
                      attribute value replaces "cta_click")
     lead_submit      { form }                     every form that reaches
                      Firestore or n8n: home_contact, home_partnership,
                      trip_interest, trip_deposit, early_access,
                      enquiry, partner_enquiry, give_learn
     kido_message     { chars }                    a question sent to Kido

   Named events from individual pages and the header (nav_*_click,
   *_cta_click, give_learn_register_submit, language_switch, ...) stay
   as they are; the site-wide ones sit beside them.

   Fire-and-forget: a blocked or failed analytics load must never
   affect navigation, so every call is wrapped and errors are dropped. */

import { getAnalyticsApi } from "../firebase";

/* Meta Pixel (base code in public/index.html). Only the events listed
   here are forwarded, under Meta's standard names, so the Give & Learn
   ads can optimise for registrations. The event id is the one the n8n
   lead intake also receives, so Meta counts a registration once if the
   same event ever arrives from the server as well. */
const PIXEL_EVENTS = { give_learn_register_submit: "CompleteRegistration", autumn_term_register_submit: "CompleteRegistration" };
const PIXEL_NAMES = { autumn_term_register_submit: "Autumn Term" };

function pagePath() {
  try { return window.location.pathname; } catch { return ""; }
}

export function track(event, params) {
  try {
    const p = { page_path: pagePath(), ...(params || {}) };
    getAnalyticsApi()
      .then(({ analytics, logEvent }) => { if (analytics) logEvent(analytics, event, p); })
      .catch(() => { /* analytics unavailable (blocked, unsupported browser): ignore */ });
  } catch {
    /* analytics unavailable (blocked, unsupported browser): ignore */
  }
  try {
    const pixelEvent = PIXEL_EVENTS[event];
    if (pixelEvent && typeof window !== "undefined" && typeof window.fbq === "function") {
      const p = params || {};
      window.fbq(
        "track",
        pixelEvent,
        { content_name: PIXEL_NAMES[event] || "Give & Learn", content_category: p.courses || "" },
        p.event_id ? { eventID: p.event_id } : undefined
      );
    }
  } catch {
    /* pixel blocked or not loaded: ignore */
  }
}

/* A route change in the single-page app. gtag counts only the page the
   visitor landed on (checked on the live site, 2026-10-10: two clicks in
   the header produced no page_view), so App.js calls this on every
   pathname change after the first. The Meta Pixel gets the same so its
   page counts match. */
export function trackPageView(path, title) {
  track("page_view", { page_path: path, page_title: title || "", page_location: safeHref() });
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") window.fbq("track", "PageView");
  } catch { /* ignore */ }
}

function safeHref() {
  try { return window.location.href; } catch { return ""; }
}

/* One listener on the document classifies every click on a link or a
   button. Nothing here changes what the click does. Text is trimmed to
   60 characters and links to 120, which is what GA4 keeps anyway. */
let clickTrackingInstalled = false;
export function installClickTracking() {
  if (clickTrackingInstalled || typeof document === "undefined") return;
  clickTrackingInstalled = true;
  document.addEventListener("click", (e) => {
    try {
      const el = e.target && e.target.closest ? e.target.closest("a, button, [data-track]") : null;
      if (!el) return;
      const named = el.getAttribute("data-track");
      const text = (el.innerText || el.getAttribute("aria-label") || "").trim().replace(/\s+/g, " ").slice(0, 60);
      const href = el.tagName === "A" ? (el.getAttribute("href") || "") : "";
      const link = href.slice(0, 120);
      if (named) { track(named, { text, link }); return; }
      if (/^https?:\/\/(api\.)?wa\.me|^https?:\/\/wa\.me|whatsapp\.com/.test(href)) { track("whatsapp_click", { text, link }); return; }
      if (/^tel:/.test(href)) { track("phone_click", { text, link }); return; }
      if (/^mailto:/.test(href)) { track("email_click", { text, link }); return; }
      if (/^https?:\/\//.test(href)) {
        let external = false;
        try { external = new URL(href).hostname !== window.location.hostname; } catch { external = false; }
        if (external) { track("outbound_click", { text, link }); return; }
      }
      const isButton = el.tagName === "BUTTON" || /(^|\s)btn(\s|-|$)/.test(el.className || "");
      if (isButton && text) track("cta_click", { text, link });
    } catch {
      /* never let tracking break a click */
    }
  }, { capture: true, passive: true });
}
